import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../config.js';

import '../../styles/global.css';
import '../../styles/requests.css';
import '../../styles/buttons.css';
import '../../styles/status.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader';
import PageTable from '../../components/general/pageTable';
import CreateWorkPermitForm from '../../components/forms/createWorkPermitForm';
import EditWorkPermitForm from '../../components/forms/editWorkPermitForm';
import DeleteConfirmation from '../../components/general/deleteConfirmation.js';
import useAuthenticate from '../../components/authentication/authenticate';
import Loading from '../../components/general/loading.js';
import { toast } from 'react-toastify';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const MWorkPermits = () => {
  const { isLoading } = useAuthenticate();
  const queryClient = useQueryClient();

  const [addOverlayOpen, setAddOverlayOpen] = useState(false);
  const [editOverlayOpen, setEditOverlayOpen] = useState(false);
  const [deleteOverlayOpen, setDeleteOverlayOpen] = useState(false);
  const [rowToEdit, setRowToEdit] = useState(null);
  const [workPermitToDelete, setWorkPermitToDelete] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const { data: properties = [] } = useQuery({
    queryKey: ['properties'],
    queryFn: fetchProperties,
  });

  useEffect(() => {
    if (!selectedProperty && properties.length > 0) {
      setSelectedProperty(properties[0]);
    }
  }, [properties, selectedProperty]);

  const { data: workPermitsData, isLoading: isRequestsLoading } = useQuery({
    queryKey: ['workPermits', selectedProperty?.value, page, rowsPerPage],
    queryFn: ({ queryKey }) => {
      const [, propertyId, currentPage, limit] = queryKey;
      return fetchWorkPermits(propertyId, currentPage, limit);
    },
    enabled: !!selectedProperty,
    keepPreviousData: true,
  });

  const { data: allUnits = [] } = useQuery({
    queryKey: ['units'],
    queryFn: fetchUnits,
  });

  const { data: propertyRoles = [] } = useQuery({
    queryKey: ['propertyAccounts', selectedProperty?.value],
    queryFn: () => fetchPropertyAccounts(selectedProperty?.value),
    enabled: !!selectedProperty,
  });

  const units = allUnits.filter((u) => u.property?._id === selectedProperty?.value);

  const handleEdit = (row) => {
    setRowToEdit(row);
    setEditOverlayOpen(true);
  };

  const handleDelete = (row) => {
    setWorkPermitToDelete(row);
    setDeleteOverlayOpen(true);
  };

  const confirmDelete = async () => {
    if (!workPermitToDelete) return;
    try {
      await axios.delete(`${config.API_URL}/v2/request/${workPermitToDelete._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      toast.success('Work permit deleted successfully');
      queryClient.invalidateQueries(['workPermits', selectedProperty?.value, page, rowsPerPage]);
      setDeleteOverlayOpen(false);
      setWorkPermitToDelete(null);
    } catch (err) {
      console.error('Error deleting work permit:', err);
      toast.error('Failed to delete work permit');
    }
  };

  const handleRequestCreated = () => {
    queryClient.invalidateQueries(['workPermits', selectedProperty?.value, page, rowsPerPage]);
    setAddOverlayOpen(false);
  };

  const handleRequestUpdated = () => {
    queryClient.invalidateQueries(['workPermits', selectedProperty?.value, page, rowsPerPage]);
    setEditOverlayOpen(false);
    setRowToEdit(null);
  };

  const totalPages = workPermitsData?.totalPages || 1;
  const maxVisibleButtons = 10;
  const startPage = Math.floor((page - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

  // === Render ===
  return (
    <div className="full-page w-full">
      <PageHeader pageName="Work Permits" />

      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* Header controls */}
          <div className="page-header">
            <Autocomplete
              options={properties}
              value={selectedProperty}
              onChange={(e, newVal) => {
                if (newVal && newVal.value && newVal.label) {
                  setSelectedProperty(newVal);
                  setPage(1);
                } else {
                  setSelectedProperty(null);
                }
              }}
              isOptionEqualToValue={(option, value) => option?.value === value?.value}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option?.label || option?.name || ''
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Property" variant="outlined" fullWidth />
              )}
              sx={{ width: '350px' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="header-button" onClick={() => setAddOverlayOpen(true)}>
                <i className="fas fa-id-card-alt"></i> File Work Permit
              </button>
            </div>
          </div>

          {/* Main table */}
          {isRequestsLoading ? (
            <Loading />
          ) : (
            <div className="table-wrapper">
              <PageTable
                rows={workPermitsData?.results || []}
                columns={[
                  { label: 'Unit', accessor: (row) => row.unit?.unitNumber || 'N/A', width: '12%' },
                  {
                    label: 'Status',
                    accessor: (row) => (
                      <span className={`request-status-badge ${row.status?.toLowerCase()}`}>
                        {row.status}
                      </span>
                    ),
                    width: '10%',
                  },
                  { label: 'Service Provider', accessor: 'details.serviceProvider', width: '15%' },
                  { label: 'Work Category', accessor: 'details.workCategory', width: '15%' },
                  {
                    label: 'Start Date',
                    accessor: (row) =>
                      row.details?.startDate
                        ? new Date(row.details.startDate).toLocaleDateString()
                        : 'N/A',
                    width: '10%',
                  },
                  {
                    label: 'End Date',
                    accessor: (row) =>
                      row.details?.endDate
                        ? new Date(row.details.endDate).toLocaleDateString()
                        : 'N/A',
                    width: '10%',
                  },
                  {
                    label: 'Filed By',
                    accessor: (row) =>
                      row.requestedBy?.user
                        ? `${row.requestedBy.user.givenName || ''} ${
                            row.requestedBy.user.lastName || ''
                          }`
                        : 'N/A',
                    width: '15%',
                  },
                ]}
                hasButtons
                hasEdit
                hasDelete
                onEdit={handleEdit}
                onDelete={handleDelete}
              />

              {workPermitsData && workPermitsData.totalPages > 1 && (
                <div className="pagination-controls">
                  <div className="pagination-buttons">
                    <button
                      className="pagination-button"
                      disabled={page === 1}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>

                    {startPage > 1 && (
                      <button className="pagination-page" onClick={() => setPage(startPage - 1)} >
                        ...
                      </button>
                    )}

                    {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                      const pageNum = startPage + i;
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {endPage < totalPages && (
                      <button className="pagination-page" onClick={() => setPage(endPage + 1)} >
                        ...
                      </button>
                    )}

                    <button
                      className="pagination-button"
                      disabled={page === totalPages}
                      onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Create Overlay */}
          {addOverlayOpen && selectedProperty && (
            <CreateWorkPermitForm
              property={{ property: { _id: selectedProperty.value } }}
              propertyId={selectedProperty.value}
              unit={units}
              propertyAccounts={propertyRoles.map((r) => r.account)}
              onRequestCreated={handleRequestCreated}
              onClose={() => setAddOverlayOpen(false)}
            />
          )}

          {/* Edit Overlay */}
          {editOverlayOpen && rowToEdit && (
            <EditWorkPermitForm
              row={rowToEdit}
              onClose={() => {
                setEditOverlayOpen(false);
                setRowToEdit(null);
              }}
              units={units}
              propertyAccounts={propertyRoles.map((r) => r.account)}
              property={{ property: { _id: selectedProperty?.value } }}
              onRequestUpdated={handleRequestUpdated}
            />
          )}

          {/* Delete Overlay */}
          {deleteOverlayOpen && workPermitToDelete && (
            <DeleteConfirmation
              setIsOverlayOpen={setDeleteOverlayOpen}
              onConfirm={confirmDelete}
            />
          )}
        </>
      )}
    </div>
  );
};

// === API HELPERS ===
const fetchProperties = async () => {
  const res = await axios.get(`${config.API_URL}/v2/property`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return res.data.map((p) => ({ value: p._id, label: p.name }));
};

const fetchWorkPermits = async (propertyId, page = 1, limit = 10) => {
  if (!propertyId) return { results: [], totalResults: 0, totalPages: 0, page: 1 };

  try {
    const res = await axios.get(`${config.API_URL}/v2/request/property/${propertyId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      params: {
        page,
        limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        type: 'WorkPermit',
      },
    });

    console.log('✅ Fetched WorkPermits:', res.data);
    return res.data;
  } catch (err) {
    console.error('❌ Error fetching work permits:', err);
    return { results: [], totalResults: 0, totalPages: 0, page: 1 };
  }
};

const fetchUnits = async () => {
  const res = await axios.get(`${config.API_URL}/v2/unit`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return res.data;
};

const fetchPropertyAccounts = async (propertyId) => {
  const res = await axios.get(
    `${config.API_URL}/v2/role/propertyRole/property/${propertyId}`,
    { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
  );
  return res.data;
};

export default MWorkPermits;
